import {flow, getEnv, types} from "mobx-state-tree";
import {TableColumn} from "../../tables/ScanbuyTable";
import Notification from "../../../utils/Notification";
import {AuditSelectState} from "../../elements/audit/AuditSelectState";

const AuditViewState = types
  .model("AuditViewState", {
    uuid: types.maybe(types.string),
    isLoading: types.optional(types.boolean, false),
    author: types.optional(types.string, ""),
    note: types.optional(types.string, ""),
    page: types.optional(types.integer, 1),
    perPage: types.optional(types.integer, 10),
    totalRows: types.optional(types.integer, 0),
    columns: types.optional(types.array(types.late(() => TableColumn)), []),
    audits: types.optional(types.array(types.frozen()), []),
    isEditMode: types.optional(types.boolean, false),
    isView: types.optional(types.boolean, false),
    formHasErrors: types.optional(types.boolean, false),
    errorMessage: types.optional(types.string, ""),
    auditSelectStore: types.maybe(types.late(() => types.reference(AuditSelectState))),
  }).volatile(self => ({
    selectedAudit: null,
    selectedCallback: types.fn,
    selectedSecondCallback: types.fn,
    selectedFetchAuditCallback: types.fn,
    selectedThirdCallback: types.fn,
  }))
  .actions((self) => ({
    handleHasSaveErrors(boolean) {
      self.formHasErrors = boolean
    },
    setOnSelectChanges() {
      self?.auditSelectStore?.setConsumer(self.setAudit);
    },
    handleSaveErrorMessage(message) {
      self.errorMessage = message
    },
    setAudit(audit) {
      self.selectedAudit = audit;
    },
    onAttach() {
      console.log("AuditStore attached to tree, fetching audits...");

      // ✅ Now this will have access to getEnv
      const manager = getEnv(self).codeManager;

      if (manager) {
        self.fetchAudit();
      } else {
        console.error("Manager not found in environment");
      }
    },
    updatePage: function (page) {
      self.page = page
    },
    updatePerPage: function (perPage) {
      self.perPage = perPage
    },
    setColumns(col) {
      self.columns = col
    },
    setAudits(audits) {
      self.audits = audits;
    },
    setNote(note) {
      self.note = note;
    },
    setUser(user) {
      self.author = user?.email;
    },
    afterCreate() {
      console.log("AuditStore created, fetching audits...");
    },
    notifyCallBack(str){
      self.selectedCallback(str)
    },
    setAuditStateListener(listener) {
      self.selectedCallback = listener
    },
    setSecondAuditStateListener(listener) {
      self.selectedFetchAuditCallback = listener
    },
    setModalListener(listener) {
      self.selectedSecondCallback = listener
    },
    notifyModalCallBack() {
      self.selectedSecondCallback();
    },
    notifyFetchAuditCallBack(str) {
      self.selectedFetchAuditCallback(str);
    },
    setSelectValue() {
      self.auditSelectStore?.setDefaultSelect();
    },
    saveAuditNotes: flow(function* () {
      if(self.isEditMode) {
        const manager = getEnv(self).codeManager;
        const resp = yield manager.saveAuditNotesToCode(self);
        if(resp.status === 200) {
          self?.notifyModalCallBack();
          !self.formHasErrors && new Notification()
            .setType("success")
            .setMessage(`Audit saved to the code`)
            .send();
        } else {
          self.handleHasSaveErrors(true);
          self.formHasErrors && new Notification()
            .setType("error")
            .setMessage(`Audit not saved to the code`)
            .send();
        }
      } else {
        self.notifyCallBack(self);
      }
    }),
    setEditMode(isEditMode) {
      self.isEditMode = isEditMode;
    },
    fetchAll: flow(function* () {

      const queryParams = {
        page: self.page,
        perPage: self.perPage,
      }

      self.isLoading = true;
      const manager = getEnv(self).codeManager;
      const resp = yield manager.getAuditForCode(self?.uuid, queryParams);
      if(resp) {
        self.audits = resp.results || [];
        self.isLoading = false;
        self.totalRows = resp._meta.itemCount;
      } else {
        self.isLoading = false
      }
    }),
    getItems() {
      return self.audits || [];
    },

  })).views((self) => ({
    isSaveDisabled() {
      const isAuditSelected = !!self.auditSelectStore?.selected?.id;
      return !isAuditSelected;
    }
  }));

export default AuditViewState;
